import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { X, Building2, Users, Loader2 } from 'lucide-react';
import { auth } from '../firebase/firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { signInWithTravelinOAuth } from '../firebase/oauth-auth';
import { ensureUserProfileExists, getRoleAndManagedStructures } from '../firebase/firestore';
import { toast } from 'sonner';

interface LoginSigninProps {
  role?: string;
  onCancel?: () => void;
  onSignIn?: (role?: string) => void;
  variant?: "modal" | "inline";
  hideClose?: boolean;
  mode?: "full" | "quick-access";
  caption?: string;
  quickAccessLabel?: string;
  onQuickAccess?: () => void | Promise<void>;
}

export function LoginSignin({ role, onCancel, onSignIn, variant = "modal", hideClose = false, mode = "full", caption, quickAccessLabel, onQuickAccess }: LoginSigninProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [quickLoading, setQuickLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [showPopupHelp, setShowPopupHelp] = useState(false);
  const [hasRetried, setHasRetried] = useState(false);
  const [showRedirectFallback, setShowRedirectFallback] = useState(false);

  const isOpeningPopup = oauthLoading;

  const resetPopupUiState = () => {
    setPopupBlocked(false);
    setShowPopupHelp(false);
    setHasRetried(false);
    setShowRedirectFallback(false);
  };

  const isPopupBlockedError = (err: any): boolean => {
    const code = err?.code;
    const message = err?.message;
    return (
      (typeof code === 'string' && code.includes('popup-blocked')) ||
      (typeof message === 'string' && message.toLowerCase().includes('popup blocked'))
    );
  };

  const handleSignIn = async () => {
    setError(null);
    if (!email || !password) {
      setError('Inserisci email e password.');
      return;
    }
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      void cred;
      onSignIn && onSignIn(role);
    } catch (err) {
      // try create account as fallback (visual flow)
      try {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        void cred;
        onSignIn && onSignIn(role);
      } catch (err2: any) {
        void err2;
        setError(err2?.message || 'Accesso non riuscito');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async () => {
    setError(null);
    resetPopupUiState();
    setOauthLoading(true);
    
    try {
      // Redirect flow - user will be redirected and handled on return
      await signInWithTravelinOAuth();
    } catch (err: any) {
      if (isPopupBlockedError(err)) {
        setPopupBlocked(true);
        setShowPopupHelp(true);
        setShowRedirectFallback(true);
        setHasRetried(true);
      }
      setError(err?.message || 'Accesso OAuth non riuscito');
    } finally {
      setOauthLoading(false);
    }
  };

  const isModal = variant === "modal";

  const handleCredentialsSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await handleSignIn();
  };

  return (
    <div className={isModal ? "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" : "w-full flex items-center justify-center"}>
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-l">Accedi{role ? ` — ${role}` : ''}</CardTitle>
              <CardDescription className="text-s">
                {caption || "Inserisci le tue credenziali per continuare."}
              </CardDescription>
            </div>
            {!hideClose && onCancel && (
              <button onClick={onCancel} className="cursor-pointer text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {mode === "full" && (
              <form className="space-y-4" onSubmit={handleCredentialsSubmit}>
                <div>
                  <label htmlFor="login-email" className="text-s text-muted-foreground">Email</label>
                  <Input id="login-email" value={email} onChange={(e) => setEmail((e.target as HTMLInputElement).value)} placeholder="tuo@azienda.com" autoComplete="email" />
                </div>

                <div>
                  <label htmlFor="login-password" className="text-s text-muted-foreground">Password</label>
                  <Input id="login-password" type="password" value={password} onChange={(e) => setPassword((e.target as HTMLInputElement).value)} placeholder="••••••••" autoComplete="current-password" />
                </div>

                {error && <div className="text-s text-red-600">{error}</div>}
                <div className="flex items-center justify-between gap-2">
                  <Button type="submit" className="flex-1" disabled={loading || oauthLoading}>
                    {loading ? 'Accesso in corso...' : 'Accedi'}
                  </Button>
                  <Button type="button" variant="outline" onClick={onCancel} className="ml-2" disabled={loading || oauthLoading}>
                    Annulla
                  </Button>
                </div>
              </form>
            )}

            <div className={mode === "full" ? "pt-2 text-center text-s text-muted-foreground" : "text-center text-s text-muted-foreground"}>
              {mode === "full" && <p>Oppure continua con</p>}
              <div className={mode === "full" ? "mt-3 space-y-2" : "mt-2 space-y-2"}>
                <Button
                  variant={mode === "full" ? "ghost" : "default"}
                  size={mode === "full" ? "sm" : "default"}
                  className={mode === "full" ? "w-full flex items-center gap-2" : "w-full"}
                  onClick={async () => {
                    if (mode === "quick-access" && onQuickAccess) {
                      if (quickLoading) return;
                      setQuickLoading(true);
                      try {
                        await onQuickAccess();
                      } finally {
                        // keep disabled to avoid double-clicks; view change will unmount
                      }
                      return;
                    }
                    await handleOAuthSignIn();
                  }}
                  disabled={loading || isOpeningPopup || quickLoading}
                >
                  {mode === "full" && <Users className="w-4 h-4" />}
                  {mode === "quick-access" && quickLoading && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {isOpeningPopup
                    ? 'Apertura finestra di accesso…'
                    : quickLoading
                      ? 'Reindirizzamento...'
                      : (quickAccessLabel || 'Accesso rapido')}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default LoginSignin;
